import React, { useState, useEffect } from "react";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Select from "react-select";
import { MdModeEdit } from "react-icons/md";
import { BASE_URL } from "../../../env";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AccountsManagerList = ({ searchQuery, refreshList }) => {
  const [accountsManagers, setAccountsManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editedUsername, setEditedUsername] = useState("");
  const [editedPassword, setEditedPassword] = useState("");
  const [editedEmployeeName, setEditedEmployeeName] = useState("");
  const [editedEmployeeId, setEditedEmployeeId] = useState("");
  const [editedBranchIds, setEditedBranchIds] = useState(null);
  const [branches, setBranches] = useState([]);

  const fetchAccountsManagers = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/accounts/managers`);
      setAccountsManagers(response.data.accountsManager || response.data);
    } catch (error) {
      console.error("Error fetching accounts managers:", error);
      toast.error("Error fetching accounts managers data");
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/getall/branches`);
      setBranches(Array.isArray(response.data) ? response.data : response.data.branches || response.data.data || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
      toast.error("Error fetching branches");
      setBranches([]);
    }
  };

  useEffect(() => {
    fetchAccountsManagers();
    fetchBranches();
  }, [refreshList]);

  const handleEdit = (manager) => {
    setSelectedManager(manager);
    setEditedUsername(manager.username);
    setEditedPassword(manager.password);
    setEditedEmployeeName(manager.employeeName);
    setEditedEmployeeId(manager.employeeId);

    // Handle multiple branch IDs
    const existingBranchIds = manager.branchId || manager.branch_id || manager.branchIds || manager.id || "";
    if (existingBranchIds) {
      const branchIdArray = typeof existingBranchIds === "string"
        ? existingBranchIds.split(",").map((id) => id.trim())
        : Array.isArray(existingBranchIds)
        ? existingBranchIds.map(id => id.toString())
        : [existingBranchIds.toString()];
      
      // Map branch IDs to react-select format
      const selectedBranches = branchIdArray
        .map(id => {
          const branch = branches.find(b => (b.id || b.branchId).toString() === id);
          return branch ? { value: (branch.id || branch.branchId).toString(), label: branch.branchName || branch.name } : null;
        })
        .filter(branch => branch !== null);
      
      setEditedBranchIds(selectedBranches);
    } else {
      setEditedBranchIds(null);
    }

    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedManager(null);
    setEditedUsername("");
    setEditedPassword("");
    setEditedEmployeeName("");
    setEditedEmployeeId("");
    setEditedBranchIds(null);
  };

  const handleBranchChange = (selectedOptions) => {
    setEditedBranchIds(selectedOptions);
  };

  const handleSaveChanges = async () => {
    if (
      !editedUsername ||
      !editedPassword ||
      !editedEmployeeName ||
      !editedEmployeeId ||
      !editedBranchIds
    ) {
      toast.error("Please fill all fields and select at least one branch");
      return;
    }

    try {
      const updateData = {
        username: editedUsername,
        password: editedPassword,
        employeeName: editedEmployeeName,
        employeeId: editedEmployeeId,
        branchId: editedBranchIds
          ? Array.isArray(editedBranchIds)
            ? editedBranchIds.map((branch) => branch.value).join(",")
            : editedBranchIds.value
          : null,
      };

      await axios.put(`${BASE_URL}/api/accounts/managers/${selectedManager.id}`, updateData);

      toast.success("Accounts manager details updated successfully!");
      fetchAccountsManagers();
      handleCloseEditModal();
    } catch (error) {
      console.error("Error updating accounts manager details:", error);
      const errorMessage = error.response?.data?.error || "Error updating accounts manager details";
      toast.error(errorMessage);
    }
  };

  const filteredManagers = accountsManagers.filter((manager) => {
    const searchTerm = searchQuery.toLowerCase();
    return [
      manager.username,
      manager.password,
      manager.branch_names,
      manager.employeeName,
      manager.employeeId,
    ].some((field) => field?.toLowerCase().includes(searchTerm));
  });

  return (
    <div className="mt-4 text-center">
      <h5 className="pb-3 TableHeaderText">Accounts Manager List</h5>
      <div className="table-responsive">
        <table className="table table-striped mx-auto table-list">
          <thead>
            <tr>
              <th>Username</th>
              <th>Password</th>
              <th>Employee Name</th>
              <th>Employee ID</th>
              <th>Branch Names</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredManagers.length > 0 ? (
              filteredManagers.map((manager) => (
                <tr key={manager.id}>
                  <td>{manager.username}</td>
                  <td>{manager.password}</td>
                  <td>{manager.employeeName}</td>
                  <td>{manager.employeeId}</td>
                  <td>
                    {manager.branch_names
                      ? manager.branch_names.split(",").map(name => name.trim()).join(", ")
                      : "No branches assigned"}
                  </td>
                  <td>
                    <button
                      className="btn btn-edit"
                      title="Edit"
                      onClick={() => handleEdit(manager)}
                    >
                      <MdModeEdit />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center">
                  No Accounts Manager Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Accounts Manager Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="editedUsername">
              <Form.Label className="pt-2">Username</Form.Label>
              <Form.Control
                type="text"
                value={editedUsername}
                onChange={(e) => setEditedUsername(e.target.value)}
                placeholder="Enter username"
              />
            </Form.Group>
            <Form.Group controlId="editedPassword">
              <Form.Label className="pt-2">Password</Form.Label>
              <Form.Control
                type="text"
                value={editedPassword}
                onChange={(e) => setEditedPassword(e.target.value)}
                placeholder="Enter password"
              />
            </Form.Group>
            <Form.Group controlId="editedEmployeeName">
              <Form.Label className="pt-2">Employee Name</Form.Label>
              <Form.Control
                type="text"
                value={editedEmployeeName}
                onChange={(e) => setEditedEmployeeName(e.target.value)}
                placeholder="Enter employee name"
              />
            </Form.Group>
            <Form.Group controlId="editedEmployeeId">
              <Form.Label className="pt-2">Employee ID</Form.Label>
              <Form.Control
                type="text"
                value={editedEmployeeId}
                onChange={(e) => setEditedEmployeeId(e.target.value)}
                placeholder="Enter employee ID"
              />
            </Form.Group>
            <Form.Group controlId="editedBranchIds">
              <Form.Label className="pt-2">Select Branches</Form.Label>
              <Select
                value={editedBranchIds}
                onChange={handleBranchChange}
                options={branches.map((branch) => ({
                  value: (branch.id || branch.branchId).toString(),
                  label: branch.branchName || branch.name,
                }))}
                isClearable
                isMulti
                onMenuOpen={fetchBranches}
                placeholder="---Select branches---"
                className="w-100"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="bg-danger btn"
            style={{ color: "white" }}
            onClick={handleCloseEditModal}
          >
            Close
          </button>
          <button
            className="btn btn-addCredential"
            onClick={handleSaveChanges}
          >
            Save Changes
          </button>
        </Modal.Footer>
      </Modal>
      <ToastContainer />
    </div>
  );
};

export default AccountsManagerList;